/**
 * Handles the Oauth client's (Applications) secret key
 * @author      Jeff Adams <jeff@tinyfly.com>
 * @copyright   Traxo, Inc. 2020
 */

import { ApplicationKeyApi } from '@/services/api-application-key.js';

// Local state -----------------------------------------------------
export const state = getDefaultState();

// Computed properties of the state --------------------------------
export const getters = {
  isCurrentApplication: (state, getters, rootState) => {
    return rootState.applications.application.id === state.id;
  },

  currentApplicationSecret: (state, getters) => {
    return getters.isCurrentApplication ? state.secret : '';
  },
};

// Synchronous mutations of the local state -------------------------
export const mutations = {
  SET_SECRET(state, { secret = '', id = '' }) {
    state.secret = secret;
    state.id = id;
  },

  RESET_STATE(state, defaultState) {
    state.secret = defaultState.secret;
    state.id = defaultState.id;
  },
};

// Async CRUD actions against the api ------------------------------
export const actions = {
  show({ commit, dispatch }, { id }) {
    return ApplicationKeyApi.show(id)
      .then((secret) => {
        if (secret) {
          commit('SET_SECRET', { secret, id });
        } else if (!secret) {
          dispatch(
            'notifications/create',
            {
              type: 'error',
              message:
                'We are unable retrieve your client secret. Try generating a new one with the "Regenerate Secret" button or try again later.',
            },
            { root: true }
          );
        }
      })
      .catch(() => {
        commit('SET_SECRET', {});
        dispatch(
          'notifications/create',
          {
            type: 'error',
            message: 'We are unable retrieve your client secret. Please try again.',
          },
          { root: true }
        );
      });
  },

  store({ commit, dispatch }, { id }) {
    return ApplicationKeyApi.store(id)
      .then((secret) => {
        commit('SET_SECRET', {
          secret: secret || '',
          id,
        });
      })
      .catch(() => {
        dispatch(
          'notifications/create',
          {
            type: 'error',
            message: 'We are unable generate your client secret. Please try again.',
          },
          { root: true }
        );
      });
  },

  /**
   * Sets the state back to the default values
   * @returns {Promise}
   */
  reset({ commit }) {
    commit('RESET_STATE', getDefaultState());
  },
};

/// ///

function getDefaultState() {
  return {
    secret: '',
    id: '',
  };
}
